import { useEffect, useState } from 'react';
import MarkdownText from './MarkdownText';
import { checkAnswerAgainstExpected } from '../utils/workAttemptHelpers';

/**
 * Поле «ваш ответ» + проверка по expected_answer + «показать ответ» (по умолчанию скрыт).
 * Проверка — клиентское сравнение строк (нормализация), не через judge/тесты.
 *
 * variant="header" — компактно в заголовке блока «Текст задачи».
 * value/onChange — опциональный controlled-режим (для сохранения попыток на /work/).
 */
export default function TaskAnswerCheckPanel({
  expectedAnswer,
  taskKey = '',
  value,
  onChange,
  onCheck,
  onReveal,
  saving = false,
  variant = 'block',
  className = '',
}) {
  const controlled = value !== undefined;
  const [internalAnswer, setInternalAnswer] = useState('');
  const [hint, setHint] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);

  const studentAnswer = controlled ? String(value ?? '') : internalAnswer;
  const hasExpected = expectedAnswer != null && String(expectedAnswer).trim() !== '';
  const isHeader = variant === 'header';

  useEffect(() => {
    if (!controlled) setInternalAnswer('');
    setHint(null);
    setShowAnswer(false);
  }, [taskKey, expectedAnswer, controlled]);

  if (!hasExpected) return null;

  function setAnswer(next) {
    if (controlled) {
      if (typeof onChange === 'function') onChange(next);
    } else {
      setInternalAnswer(next);
    }
    setHint(null);
  }

  function handleCheck() {
    const ok = checkAnswerAgainstExpected(studentAnswer, expectedAnswer);
    setHint(ok ? 'ok' : 'wrong');
    if (typeof onCheck === 'function') {
      void onCheck({ ok, studentAnswer });
    }
  }

  function handleRevealToggle() {
    if (showAnswer) {
      setShowAnswer(false);
      return;
    }
    setShowAnswer(true);
    if (typeof onReveal === 'function') {
      void onReveal({ studentAnswer });
    }
  }

  function stopDrag(e) {
    e.stopPropagation();
  }

  const hintNode =
    hint === 'ok' ? (
      <span className="work-answer-hint work-answer-hint-ok">Верно</span>
    ) : hint === 'wrong' ? (
      <span className="work-answer-hint work-answer-hint-wrong">Неверно</span>
    ) : null;

  const revealNode = showAnswer ? (
    <div className={`answer-block task-answer-check-reveal ${isHeader ? 'task-answer-check-reveal--header' : ''}`.trim()}>
      <MarkdownText className="statement-text answer-text" text={String(expectedAnswer)} />
    </div>
  ) : null;

  if (isHeader) {
    return (
      <div
        className={`task-answer-check-header ${className}`.trim()}
        onMouseDown={stopDrag}
        onPointerDown={stopDrag}
        onClick={stopDrag}
      >
        <input
          type="text"
          className="form-input task-answer-check-header-input"
          value={studentAnswer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Ответ"
          aria-label="Ваш ответ"
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCheck();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline btn-xs"
          disabled={saving}
          onClick={handleCheck}
        >
          {saving ? '…' : 'Проверить'}
        </button>
        <button
          type="button"
          className="btn btn-outline btn-xs"
          disabled={saving && !showAnswer}
          onClick={handleRevealToggle}
        >
          {showAnswer ? 'Скрыть' : saving ? '…' : 'Показать'}
        </button>
        {hintNode}
        {revealNode}
      </div>
    );
  }

  return (
    <div className={`work-student-answer-block ${className}`.trim()}>
      <label className="work-student-answer-label">Ваш ответ</label>
      <input
        type="text"
        className="form-input work-student-answer-input"
        value={studentAnswer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Введите ответ для проверки"
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleCheck();
          }
        }}
      />
      <div className="work-student-answer-actions">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={saving}
          onClick={handleCheck}
        >
          {saving ? '…' : 'Проверить'}
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={saving && !showAnswer}
          onClick={handleRevealToggle}
        >
          {showAnswer ? 'Скрыть ответ' : saving ? '…' : 'Показать ответ'}
        </button>
        {hintNode}
      </div>
      {revealNode}
    </div>
  );
}
